import { useState } from "react";
import { post } from "../../utils/api";
import { toast } from "react-toastify";
import InputText from "../../customComponents/InputText";

const GridModal = ({ editTarget, groups, onClose, onSaved }) => {
  const prefilledGroups = (editTarget?.group_ids || [])
    .map((gid) => groups.find((g) => g.id === gid))
    .filter(Boolean);

  const [title, setTitle] = useState(editTarget?.title || "");
  const [isActive, setIsActive] = useState(editTarget?.is_active ?? true);
  const displayOrder = editTarget?.display_order ?? 0;
  const [selectedGroups, setSelectedGroups] = useState(prefilledGroups);
  const [groupSearch, setGroupSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const availableGroups = groups.filter(
    (g) =>
      !selectedGroups.find((sg) => sg.id === g.id) &&
      g.name.toLowerCase().includes(groupSearch.toLowerCase()),
  );

  const addGroup = (group) => {
    setSelectedGroups((prev) => [...prev, group]);
    setGroupSearch("");
  };

  const removeGroup = (id) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== id));
  };

  const moveGroupUp = (index) => {
    if (index === 0) return;
    const next = [...selectedGroups];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    setSelectedGroups(next);
  };

  const moveGroupDown = (index) => {
    if (index === selectedGroups.length - 1) return;
    const next = [...selectedGroups];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    setSelectedGroups(next);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...(editTarget?.id ? { id: editTarget.id } : {}),
        title: title.trim(),
        is_active: isActive,
        display_order: Number(displayOrder) || 0,
        groups: selectedGroups.map((g, i) => ({
          group_id: g.id,
          display_order: i,
        })),
      };
      await post("/cms/grid/save", payload);
      toast.success(editTarget ? "Section updated" : "Section created");
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">
            {editTarget ? "Edit Section" : "Add Section"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            ×
          </button>
        </div>

        <div className="space-y-4">
          <InputText
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Explore by Destination"
          />



          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="grid-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="grid-active" className="text-sm text-gray-700">
              Active
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Groups
            </label>
            <input
              type="text"
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search categories to add..."
              className="w-full h-[40px] border border-gray-300 rounded-md px-3 text-sm text-gray-700 mb-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {groupSearch && availableGroups.length > 0 && (
              <div className="border border-gray-200 rounded-md max-h-32 overflow-y-auto mb-3">
                {availableGroups.map((g) => (
                  <div
                    key={g.id}
                    className="flex justify-between items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-0"
                    onClick={() => addGroup(g)}
                  >
                    <span className="text-gray-800">{g.name}</span>
                    <span className="text-gray-400 text-xs">
                      {g.items?.length ?? 0} items
                    </span>
                  </div>
                ))}
              </div>
            )}
            {groupSearch && availableGroups.length === 0 && (
              <p className="text-xs text-gray-400 mb-3">No matching groups.</p>
            )}
            {selectedGroups.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-100 overflow-hidden">
                {selectedGroups.map((g, index) => (
                  <div
                    key={g.id}
                    className="flex items-center gap-2 px-2 py-1.5 bg-white hover:bg-gray-50 text-sm"
                  >
                    <span className="text-gray-300 text-xs w-4 shrink-0 text-center">{index + 1}</span>
                    <span className="flex-1 text-gray-800 truncate">{g.name}</span>
                    <span className="text-gray-400 text-xs shrink-0">{g.items?.length ?? 0} items</span>
                    <div className="flex items-center gap-0.5 shrink-0 ml-1">
                      <button
                        type="button"
                        onClick={() => moveGroupUp(index)}
                        disabled={index === 0}
                        className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>arrow_upward</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => moveGroupDown(index)}
                        disabled={index === selectedGroups.length - 1}
                        className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>arrow_downward</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeGroup(g.id)}
                        className="p-0.5 text-red-400 hover:text-red-600 ml-0.5"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>close</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GridModal;
